import {
  type ComponentProps,
  type MouseEventHandler,
  useEffect,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { Box } from "@hightouch/htui";
import {
  rawAgentFileUrlFallback,
  rawAgentFileUrlForAdRef,
} from "@hightouch/lib/ad-studio/html-editor/resolve-ad-refs";
import { AdStudioImage } from "./ad-studio-image";
import { boundedResizeDimensions } from "src/pages/ad-studio/components/resize-dimensions";
import { AD_CSS_ISOLATION_CLASS, AdCssIsolation } from "./ad-css-isolation";
import type { AdAssetPreviewAsset } from "src/pages/ad-studio/components/ad-asset-kind";
import { directUrlsForRendition } from "src/pages/ad-studio/components/direct-image-urls";
import { prepareAdContentForDisplay } from "src/pages/ad-studio/html-ad-editor/prepare-ad-content-for-display";
import { useRegionConfig } from "src/utils/use-region-config";

export type AdAssetProps = ComponentProps<typeof AdStudioImage> & {
  /**
   * Saved editor content. May be HTML (new static-ad format) or legacy SVG —
   * `prepareAdContentForDisplay` normalizes both into HTML before render.
   * Falls through to `<AdStudioImage src={...}>` when null/undefined or when
   * the content can't be parsed.
   */
  svg?: string | null;
  componentAssets?: Record<string, string> | null;
  /**
   * Signed CDN URLs the row already carries for its `componentAssets`. Lets
   * the images in `svg` paint without the `/agents/files` redirect hop.
   */
  componentAssetUrls?: AdAssetPreviewAsset["component_asset_urls"];
  objectFit?: "contain" | "cover";
  /**
   * Set by a caller whose list already unmounts offscreen items. It drops
   * `content-visibility`, which would otherwise skip rendering an item the
   * virtualizer deliberately keeps mounted inside its overscan and re-render it
   * on re-entry, so the tile visibly pops back in.
   *
   * Left unset everywhere else: most surfaces that render markup are plain
   * lists (the editor rail holds up to 200), and there the skipping is what
   * keeps offscreen vector recordings out of style, layout, and paint.
   */
  isVirtualized?: boolean;
};

export function AdAsset({
  svg,
  componentAssets,
  componentAssetUrls,
  objectFit,
  isVirtualized,
  ...rest
}: AdAssetProps) {
  const { apiEndpoint } = useRegionConfig();
  const instanceId = useId();
  const { naturalWidth, naturalHeight, format = "webp" } = rest;

  const prepared = useMemo(() => {
    if (!svg) return null;
    // Every `ad:` ref in the markup resolves to its own /agents/files request,
    // so an unbounded caller would ask the resizer to transcode each component
    // image at full resolution.
    const { width, height } = boundedResizeDimensions(
      naturalWidth,
      naturalHeight,
    );
    return prepareAdContentForDisplay(
      svg,
      componentAssets,
      apiEndpoint,
      {
        width,
        height,
        format,
        targetRenderer: "hightouch-webapp",
        directUrls: directUrlsForRendition({
          componentAssetUrls,
          width: naturalWidth,
          height: naturalHeight,
          format,
        }),
      },
      undefined,
      `ad${instanceId}`,
    );
  }, [
    instanceId,
    svg,
    componentAssets,
    componentAssetUrls,
    apiEndpoint,
    naturalWidth,
    naturalHeight,
    format,
  ]);

  if (prepared) {
    return (
      <FittedHtmlContainer
        html={prepared.html}
        dimensions={prepared.dimensions}
        componentAssets={componentAssets}
        apiBase={apiEndpoint}
        width={rest.width}
        height={rest.height}
        maxWidth={rest.maxWidth}
        maxHeight={rest.maxHeight}
        objectFit={objectFit}
        isVirtualized={isVirtualized}
        onContentClick={rest.onClick}
      />
    );
  }

  return <AdStudioImage objectFit={objectFit} {...rest} />;
}

type BoxProps = ComponentProps<typeof Box>;

/**
 * The box's size as laid out, which a transform on an ancestor (a zoomed
 * canvas) leaves alone. Its rect is the size on screen, so on a canvas at a
 * third of its size the fit came out a third too small until the observer
 * reported.
 */
function layoutSize(el: HTMLElement) {
  const rect = el.getBoundingClientRect();
  // An unrendered box's style holds the declared value ("100%"), not a used size.
  if (rect.width === 0 || rect.height === 0) {
    return { width: rect.width, height: rect.height };
  }
  const style = getComputedStyle(el);
  return {
    width: Number.parseFloat(style.width),
    height: Number.parseFloat(style.height),
  };
}

/**
 * Renders prepared editor HTML at its natural pixel size and uses
 * `transform: scale` to fit it into a thumbnail box. Necessary because the
 * editor's HTML output has explicit pixel widths/heights on the root and
 * absolute-positioned children — neither responds to a width/height override.
 */
function FittedHtmlContainer({
  html,
  dimensions,
  componentAssets,
  apiBase,
  width,
  height,
  maxWidth,
  maxHeight,
  objectFit,
  isVirtualized,
  onContentClick,
}: {
  html: string;
  dimensions: { width: number; height: number } | null;
  /** Resolve a failed direct CDN `<img>` back to its raw object via `data-ad-ref`. */
  componentAssets?: Record<string, string> | null;
  apiBase: string;
  width?: BoxProps["width"];
  height?: BoxProps["height"];
  maxWidth?: BoxProps["maxWidth"];
  maxHeight?: BoxProps["maxHeight"];
  objectFit?: "contain" | "cover";
  isVirtualized?: boolean;
  /** onClick placed on the actual rendered content box, not the letterbox container. */
  onContentClick?: MouseEventHandler<HTMLElement>;
}) {
  const outerRef = useRef<HTMLDivElement>(null);
  const [containerSize, setContainerSize] = useState<{
    w: number;
    h: number;
  } | null>(null);

  // A layout effect with its own first measure, rather than waiting on the
  // observer: both a `useEffect` and the observer's initial callback land after
  // paint, so every mount painted one empty frame before the markup appeared.
  // The grid virtualizer remounts a card each time it scrolls back into range,
  // which made that frame read as a gray tile that then popped in.
  useLayoutEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    // Zero means the box has no layout yet (a display:none ancestor); leaving
    // the size unset keeps the markup out of the DOM until it has somewhere to
    // paint, which is what the observer-only version did.
    const apply = (w: number, h: number) => {
      if (w === 0 || h === 0) return;
      setContainerSize((prev) =>
        prev && prev.w === w && prev.h === h ? prev : { w, h },
      );
    };
    // This Box carries no padding or border, so its layout size and the
    // observer's content box agree.
    const size = layoutSize(el);
    apply(size.width, size.height);
    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        apply(entry.contentRect.width, entry.contentRect.height);
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // imgproxy rejects sources above its max resolution (e.g. an oversized brand
  // logo), so a resized `<img>` in the injected markup fails to load even
  // though the raw object is fine — the same image renders on the editor
  // canvas, which never requests a resize. `error` doesn't bubble, so listen in
  // the capture phase on the container to catch failures from the
  // dangerouslySetInnerHTML `<img>`s and retry each once against the raw
  // (unresized) URL, mirroring the editor's path.
  useEffect(() => {
    const el = outerRef.current;
    if (!el) return;
    const handleImageError = (event: Event) => {
      const target = event.target;
      if (!(target instanceof HTMLImageElement)) return;
      if (target.dataset.rawFallbackApplied === "true") return;
      // A direct CDN src carries no API URL to strip, so recover the raw
      // object from the ad: ref the resolver left on the element.
      const fallback =
        rawAgentFileUrlFallback(target.src) ??
        (target.dataset.adRef
          ? rawAgentFileUrlForAdRef(
              target.dataset.adRef,
              componentAssets,
              apiBase,
            )
          : null);
      if (!fallback || fallback === target.src) return;
      target.dataset.rawFallbackApplied = "true";
      target.src = fallback;
    };
    el.addEventListener("error", handleImageError, true);
    return () => el.removeEventListener("error", handleImageError, true);
  }, [componentAssets, apiBase]);

  const fit = useMemo(() => {
    if (!dimensions || !containerSize) return null;
    const scaleFn = objectFit === "cover" ? Math.max : Math.min;
    const scale = scaleFn(
      containerSize.w / dimensions.width,
      containerSize.h / dimensions.height,
    );
    return {
      scale,
      width: Math.round(dimensions.width * scale),
      height: Math.round(dimensions.height * scale),
    };
  }, [dimensions, containerSize, objectFit]);

  // Thumbnails (cover-fit, no explicit height) reserve the ad's natural
  // aspect ratio up front, so the tile has its final height on first paint —
  // before the measure tick or any image loads. Masonry grids measure tiles
  // to place them, so an unreserved (0-tall) first paint would make items
  // settle into different columns a frame later. Contain-fit ("full") callers
  // own their container, where a forced ratio would fight maxHeight.
  const reservedAspectRatio =
    dimensions && objectFit === "cover" && height == null
      ? `${dimensions.width} / ${dimensions.height}`
      : undefined;

  return (
    <Box
      ref={outerRef}
      width={width}
      height={height}
      maxWidth={maxWidth}
      maxHeight={maxHeight}
      overflow="hidden"
      display="flex"
      alignItems="center"
      justifyContent="center"
      style={
        reservedAspectRatio ? { aspectRatio: reservedAspectRatio } : undefined
      }
    >
      {fit && dimensions && (
        <Box
          width={`${fit.width}px`}
          height={`${fit.height}px`}
          flexShrink={0}
          overflow="hidden"
          position="relative"
          // Markup paints as huge vector recordings (2-10MB Skia recordings of
          // brand-font glyph paths), so hover-driven compositor churn used to
          // re-raster megabytes and make components blink across the grid
          // (#42490). `will-change` pins each thumbnail to its own cached layer
          // and `contain: strict` keeps overlap recomputation from cascading
          // into the markup. Size containment is safe: the box is sized above.
          //
          // That fix also carried `content-visibility: auto`, which shrank the
          // cascade by skipping offscreen thumbnails. A virtualized list already
          // unmounts those, and the skipping then works against its overscan:
          // the tile it kept mounted renders only on re-entry, so it pops in.
          // Plain lists keep it, which is most of them (#42414 virtualized the
          // grids a day before #42490 landed, and nothing else since).
          sx={{
            contain: "strict",
            willChange: "transform",
            ...(isVirtualized
              ? {}
              : {
                  contentVisibility: "auto",
                  containIntrinsicSize: `${fit.width}px ${fit.height}px`,
                }),
          }}
          onClick={onContentClick}
        >
          <AdCssIsolation />
          <Box
            // Isolate the injected ad markup from the app's global CSS (notably
            // `img { max-width: 100% }`), which would otherwise clamp an
            // over-canvas background and shift its object-fit crop. The class
            // goes on this wrapper so the reset hits only the injected markup,
            // not this Chakra element's own (class-based) styles.
            className={AD_CSS_ISOLATION_CLASS}
            position="absolute"
            top={0}
            left={0}
            width={`${dimensions.width}px`}
            height={`${dimensions.height}px`}
            sx={{
              transform: `scale(${fit.scale})`,
              transformOrigin: "top left",
            }}
            // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from our own database, not user input
            dangerouslySetInnerHTML={{ __html: html }}
          />
        </Box>
      )}
    </Box>
  );
}
